import { useState } from "react";
import { feedItems, hotComments, type Comment } from "@/mocks/home";

function Likes({ init, filled }: { init: string; filled: boolean }) {
  return (
    <span className={"inline-flex items-center gap-1 text-xs " + (filled ? "text-primary-600" : "text-foreground-500")}>
      <i className={filled ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true" />
      {init}
    </span>
  );
}

const toneStyle: Record<Comment["tone"], string> = {
  praise: "bg-primary-100 text-primary-700",
  hot: "bg-accent-100 text-accent-800",
  funny: "bg-secondary-100 text-secondary-900",
};

const toneLabel: Record<Comment["tone"], string> = {
  praise: "实力派",
  hot: "热浪涌",
  funny: "嘴替担当",
};

function FeedArea() {
  const [feedLikes, setFeedLikes] = useState<boolean[]>(() => feedItems.map(() => false));
  const [commentLikes, setCommentLikes] = useState<boolean[]>(() => hotComments.map(() => false));

  return (
    <section id="feed" className="w-full border-y border-background-200 bg-background-100 py-14 md:py-20">
      <div className="shell grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* ---------------- left timeline ---------------- */}
        <div className="lg:col-span-2">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="sec-eyebrow">Live gossip timeline</p>
              <h2 className="mt-3 flex items-center gap-3 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
                今日瓜田时间线
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-2.5 py-1 text-xs font-bold text-background-50">
                  <span className="inline-block h-1.5 w-1.5 animate-pulseDot rounded-full bg-background-50" />
                  LIVE
                </span>
              </h2>
            </div>
            <div className="chip w-fit">
              <i className="ri-time-line mr-1 text-sm text-primary-500" aria-hidden="true" />
              快讯每 10 分钟刷新
            </div>
          </div>

          <div className="feed-scroll mt-7 space-y-4 lg:max-h-[720px] lg:overflow-y-auto lg:pr-1">
            {feedItems.map((f, i) => (
              <article
                key={f.title}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3.5 md:gap-5 md:p-4"
              >
                <div className="hidden shrink-0 flex-col items-center pt-1 sm:flex">
                  <span className="font-display text-sm font-bold tracking-wide text-primary-600">{f.time}</span>
                  <span className="mt-1.5 h-full w-px bg-background-200" />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {f.hot && (
                      <span className="inline-flex items-center gap-0.5 rounded bg-primary-100 px-1.5 py-0.5 text-[11px] font-bold text-primary-700">
                        <i className="ri-fire-fill text-xs" aria-hidden="true" />
                        HOT
                      </span>
                    )}
                    <span className={"text-xs font-bold " + f.catColor}>{f.cat}</span>
                    <span className="hidden text-xs text-foreground-400 sm:block">{f.time}</span>
                  </div>
                  <h3 className="mt-1.5 font-heading text-base font-black leading-snug text-foreground-950 md:text-lg">
                    {f.title}
                  </h3>
                  <p className="mt-1 text-[13px] leading-relaxed text-foreground-600">{f.text}</p>

                  <div className="mt-3 flex items-start gap-2.5 rounded-md bg-secondary-100/70 p-2.5">
                    <i className="ri-chat-1-line mt-0.5 shrink-0 text-sm text-secondary-500" aria-hidden="true" />
                    <p className="text-xs italic leading-relaxed text-secondary-900">
                      {f.comment}
                    </p>
                  </div>

                  <div className="mt-3 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        setFeedLikes((prev) => prev.map((v, idx) => (idx === i ? !v : v)))
                      }
                      className="inline-flex items-center gap-1.5 rounded-md border border-background-200 px-2.5 py-1 text-xs text-foreground-600 hover:border-primary-300 hover:text-primary-600 cursor-pointer transition-colors"
                      aria-pressed={feedLikes[i]}
                    >
                      <Likes init={f.likes} filled={feedLikes[i]} />
                    </button>
                    <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-reply-line text-sm" aria-hidden="true" />
                      {f.replies}
                    </span>
                    <a href="#hot" className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-primary-600 hover:text-primary-700 cursor-pointer">
                      加入热评
                      <i className="ri-arrow-right-line" aria-hidden="true" />
                    </a>
                  </div>
                </div>

                <img
                  src={f.image}
                  alt={f.title}
                  className="hidden h-[104px] w-[120px] shrink-0 rounded-md object-cover md:block"
                />
              </article>
            ))}
          </div>

          <a
            href="#play"
            className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-primary-600 hover:text-primary-700 cursor-pointer"
          >
            <i className="ri-refresh-line" aria-hidden="true" />
            查看更多快讯
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </a>
        </div>

        {/* ---------------- right hot comments ---------------- */}
        <aside id="hot" className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-lg border border-accent-200 bg-accent-50/60 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-label text-xs font-bold uppercase tracking-[0.24em] text-accent-700">
                  Hot comments
                </p>
                <h3 className="mt-2 font-heading text-xl font-black text-foreground-950">热评现场</h3>
              </div>
              <span className="grid h-11 w-11 items-center justify-center rounded-full bg-accent-400 text-primary-950">
                <i className="ri-chat-smile-3-line text-xl" aria-hidden="true" />
              </span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600">
              本期共收到 12.7 万条讨论，选出最热闹的几条，看看你被谁戳中笑点。
            </p>

            <div className="mt-5 space-y-3">
              {hotComments.map((c, i) => (
                <div key={c.user} className="rounded-lg border border-background-100 bg-background-50 p-3.5">
                  <div className="flex items-center gap-2.5">
                    <span className="grid h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-background-50">
                      {c.user.charAt(0)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-bold text-foreground-950">{c.user}</span>
                        <span className={"inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-bold " + toneStyle[c.tone]}>
                          {toneLabel[c.tone]}
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-foreground-400">
                        <span>{c.tag}</span>
                        <span className="h-3 w-px bg-background-200" />
                        <span>{c.level}</span>
                      </div>
                    </div>
                  </div>
                  <p className="mt-2.5 text-[13px] leading-relaxed text-foreground-700">{c.text}</p>
                  <div className="mt-3 flex items-center gap-3 border-t border-background-100 pt-2.5">
                    <button
                      type="button"
                      onClick={() => setCommentLikes((prev) => prev.map((v, idx) => (idx === i ? !v : v)))}
                      className="inline-flex items-center gap-1 text-xs cursor-pointer transition-colors"
                      aria-pressed={commentLikes[i]}
                    >
                      <i
                        className={(commentLikes[i] ? "ri-thumb-up-fill " : "ri-thumb-up-line ") + (commentLikes[i] ? "text-primary-600" : "text-foreground-500")}
                        aria-hidden="true"
                      />
                      <span className={commentLikes[i] ? "text-primary-600" : "text-foreground-500"}>{c.likes}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => undefined}
                      className="inline-flex items-center gap-1 text-xs text-foreground-500 hover:text-primary-600 cursor-pointer transition-colors"
                    >
                      <i className="ri-message-2-line" aria-hidden="true" />
                      {c.replies}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="btn-primary mt-4 w-full cursor-pointer"
              onClick={() => undefined}
            >
              <i className="ri-send-plane-line mr-1.5 text-base" aria-hidden="true" />
              去瓜田聊两句
            </button>
          </div>
        </aside>
      </div>
    </section>
  );
}

export default FeedArea;